// F-U: a "YYYY-MM-DD" date field (F-AC D1 — every staff date picker uses it;
// it showed "YYYY/MM/DD" until 2026-10-08). Staff can type (dashes are
// inserted as they go) or pick from the browser's native calendar via the
// icon. The text box always shows YYYY-MM-DD regardless of the browser's
// locale, which a bare <input type="date"> can't guarantee.

import { useRef, type FC } from "react";
import { CalendarDays } from "lucide-react";
import {
  formatPeriodDate,
  maskPeriodInput,
  parsePeriodDate,
} from "@shared/lib/period";

export interface DateFieldProps {
  /** Display text, "YYYY-MM-DD" (possibly partial while typing). */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  ariaLabel: string;
  testId: string;
}

const DateField: FC<DateFieldProps> = ({
  value,
  onChange,
  disabled = false,
  invalid = false,
  ariaLabel,
  testId,
}) => {
  const pickerRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    // showPicker: Chrome 99+, Safari 16+, Firefox 101+. Older browsers can
    // still type the date.
    try {
      el.showPicker();
    } catch {
      el.focus();
    }
  };

  return (
    <div className="relative inline-flex items-center">
      <input
        type="text"
        inputMode="numeric"
        placeholder="YYYY-MM-DD"
        value={value}
        onChange={(e) => onChange(maskPeriodInput(e.target.value))}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        className={`w-36 border rounded px-3 py-2 pr-9 font-mono text-sm disabled:bg-muted disabled:text-muted-foreground ${
          invalid ? "border-red-500" : ""
        }`}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        className="absolute right-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
        aria-label={`Pick ${ariaLabel.toLowerCase()} from calendar`}
        data-testid={`${testId}-picker`}
      >
        <CalendarDays className="w-4 h-4" />
      </button>
      {/* Invisible native picker; its value is ISO, mapped to display text. */}
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute right-0 bottom-0 w-0 h-0 opacity-0 pointer-events-none"
        value={parsePeriodDate(value) ?? ""}
        onChange={(e) => onChange(formatPeriodDate(e.target.value))}
        data-testid={`${testId}-native`}
      />
    </div>
  );
};

export default DateField;
